<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Icon } from '@components'
import { sortedDays, toRGB } from '@utils'
import moment from 'moment'

import { useAppointments } from '@store/appointments.js'
const $appointments = useAppointments()

import { useSettings } from '@store/settings.js'
const $settings = useSettings()

import { useCustomizer } from '@store/customizer.js'
const $customizer = useCustomizer()

// emit
const emit = defineEmits(['update:modelValue', 'change'])

// props
const props = defineProps({
    futureDays: {
        type: Number,
        default: 90
    }
})

const activeDate = ref({})

// original days array according to [new Date()]
const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',]

const activeCalender = ref({
    month: 1, // specify the month (1 for January, 2 for February, etc.)
    year: 0, // specify the year
    dates: [],
})

const generateDates = () => {
    const selectedDate = moment($appointments.selectedDate.fullDate, 'YYYY-MM-DD')
    const startDayOfCalenderWeek = sortedDays()[0]

    activeCalender.value.dates = []

    let dayToAddStart = (selectedDate.day() + 7 - days.indexOf(startDayOfCalenderWeek)) % 7
    let dateStart = selectedDate.subtract(dayToAddStart, 'days')

    for (let i = 0; i < 7; i++) {
        activeCalender.value.dates.push({
            day: dateStart.format('dddd'),
            date: dateStart.date(),
            month: dateStart.format('MMMM'),
            year: dateStart.format('YYYY'),
            fullDate: dateStart.format('YYYY-MM-DD'),
        })

        dateStart.add(1, 'days')
    }

}


onMounted(() => {
    if (!$appointments.selectedDate) {
        // set the current month and year
        activeCalender.value.month = moment().format('MM')
        activeCalender.value.year = moment().format('YYYY')
    } else {
        // if date is selected then set the active date
        activeCalender.value.year = moment($appointments.selectedDate.fullDate, 'YYYY-MM-DD').format('YYYY')
        activeCalender.value.month = moment($appointments.selectedDate.fullDate, 'YYYY-MM-DD').format('MM')
        activeDate.value = $appointments.selectedDate
    }
})


const getDates = computed(() => {
    generateDates()
    return activeCalender.value.dates
})

// const isFuture = (fullDate) => {
//     const startDate = moment().format('YYYY-MM-DD')
//     const endDate = moment(fullDate, 'YYYY-MM-DD')

//     const differenceInDays = endDate.diff(startDate, 'days')

//     if (differenceInDays > props.futureDays) {
//         return true
//     } else {
//         return false
//     }
// }

const selectDate = (item) => {
    if (!item) return
    if (isDisabled(item)) return
    if (item.fullDate == "") return

    activeDate.value = item

    emit('update:modelValue', activeDate.value)
    emit('change', activeDate.value)
}

const isActive = (item) => {
    if (!item) return
    return item.fullDate == activeDate.value.fullDate
}

const isDisabled = (item) => {
    // Check if the date is in availableDates.
    const availableDates = $appointments.availableDates
    if (moment(item.month, 'MMMM').isSame(moment(activeCalender.value.month, 'MM'), 'month') == false) return true
    if (availableDates.length > 0) {
        const findDate = availableDates.find(date => moment(item.fullDate, 'YYYY-MM-DD').isSame(moment(date.date, 'YYYY-MM-DD'), 'day'))
        if (!findDate) return true
    }
}

const progressHeight = (item) => {
    let findDate = $appointments.availableDates.find(date => item.fullDate == date.date)

    if (findDate) return Math.round((findDate.booked / findDate.total) * 100)
    else return 0
}

watch(() => $appointments.selectedDate, () => {
    if ($appointments.selectedDate) {
        activeDate.value = $appointments.selectedDate
    }
})
</script>

<template>
    <div class="mini-calendar">
        <!-- calendar header  -->
        <div class="mini-calendar__header">
            <div class="mini-calendar__month">
                {{ moment(`${activeCalender.year}-${activeCalender.month}-01`, 'YYYY-MM').format('MMMM YYYY') || 'N/A' }}
            </div>
            <Icon 
                name="calendar" 
                size="20px" 
                class="mini-calendar__icon"
                @click="$appointments.activeStep = 'date'" />
        </div>

        <!-- divider  -->
        <div class="mini-calendar__divider"></div>

        <div class="mini-calendar__content">
            <div class="mini-calendar__container">
                <!-- calendar days  -->
                <div class="mini-calendar__days">
                    <div v-for="day in sortedDays()" 
                         :key="day" 
                         class="mini-calendar__days-item">
                        {{ day.slice(0, 3) }}
                    </div>
                </div>

                <!-- calendar dates  -->
                <div class="mini-calendar__dates">
                    <div v-for="item in getDates" 
                         :key="item"
                         @click="selectDate(item)"
                         class="mini-calendar__dates-cell"
                         :class="{
                             'mini-calendar__dates-cell--disabled': isDisabled(item),
                             'mini-calendar__dates-cell--active': isActive(item)
                         }"
                         :style="`
                             width: ${$settings.isWidget ? '32px' : ''};
                             background-color: ${isActive(item) ? `${$settings.customizer.design.brand_color} !important` : ''};
                             border: 1px solid ${isActive(item) ? `${$settings.customizer.design.brand_color}` : ''} !important;
                             border-color: ${isDisabled(item) ? 'transparent' : `${toRGB($settings.customizer.design.brand_color, 0.3)}`} !important;
                             border-radius: ${$customizer.isRounded ? '4px' : '0px'} !important;
                         `">
                        <div v-if="!isActive(item)"
                             class="mini-calendar__dates-cell-progress"
                             :style="`
                                 height: ${progressHeight(item)}%;
                                 background-color: ${$settings.customizer.date.progress_color};
                             `">
                        </div>
                        <div class="mini-calendar__dates-cell-text">{{ item.date }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>